import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { ProductRelatedSection, ProductSampleModal } from "./productDetailParts";
import { t } from "../../i18n";

function formatPrice(value, currency) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2
  }).format(value);
}

function formatPriceParts(value, currency) {
  const amount = Number(value || 0);
  const safeValue = Number.isFinite(amount) ? amount : 0;
  const parts = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).formatToParts(safeValue);

  return {
    symbol: parts.find((item) => item.type === "currency")?.value || "$",
    integer: parts.find((item) => item.type === "integer")?.value || "0",
    fraction: parts.find((item) => item.type === "fraction")?.value || "00"
  };
}

function extractOssObjectKey(rawValue) {
  const raw = String(rawValue || "").trim();
  if (!raw) {
    return "";
  }

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      const host = String(parsed.hostname || "").toLowerCase();
      const isAliyunOss = host.includes("aliyuncs.com") && host.includes("oss");
      if (!isAliyunOss) {
        return "";
      }

      return decodeURIComponent(String(parsed.pathname || "")).replace(/^\/+/, "");
    } catch (_err) {
      return "";
    }
  }

  if (raw.startsWith("/oss/")) {
    return "";
  }

  const normalized = raw.replace(/^\/+/, "");
  if (!normalized || normalized.includes("..") || normalized.includes("\\")) {
    return "";
  }

  return normalized;
}

function maskReviewerName(name) {
  const text = String(name || "").trim();
  if (!text) {
    return "A***n";
  }
  if (text.length <= 2) {
    return `${text[0] || "A"}***${text[text.length - 1] || "n"}`;
  }
  return `${text.slice(0, 1)}***${text.slice(-1)}`;
}

function formatReviewDate(value) {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) {
    return String(value || "").slice(0, 10) || "-";
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(date);
}

function renderTemplate(key, params = {}) {
  const template = String(t(key));
  return template.replace(/\{(\w+)\}/g, (_match, token) => {
    if (Object.prototype.hasOwnProperty.call(params, token)) {
      return String(params[token]);
    }
    return "";
  });
}

export default function ProductDetailPage({ apiBaseUrl, storefrontLocale = "en", storefrontContacts = {}, pageTemplateConfig = {} }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const mainImageRef = useRef(null);
  const videoRef = useRef(null);
  const contentVideoRef = useRef(null);
  const relatedTrackRef = useRef(null);
  const [currency, setCurrency] = useState("USD");
  const [product, setProduct] = useState(null);
  const [productSpecs, setProductSpecs] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeImage, setActiveImage] = useState("");
  const [activeMediaType, setActiveMediaType] = useState("image");
  const [activeInfoTab, setActiveInfoTab] = useState("attributes");
  const [showPricing, setShowPricing] = useState(false);
  const [zoomVisible, setZoomVisible] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [zoomLens, setZoomLens] = useState({ left: 36, top: 22, width: 28, height: 36 });
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [sampleQuantity, setSampleQuantity] = useState(1);
  const [sampleSent, setSampleSent] = useState(false);
  const [sampleSubmitting, setSampleSubmitting] = useState(false);
  const [sampleSubmitError, setSampleSubmitError] = useState("");
  const [videoLoadError, setVideoLoadError] = useState(false);
  const [selectedSkuCode, setSelectedSkuCode] = useState("");
  const [selectedOptionValueIds, setSelectedOptionValueIds] = useState({});
  const [selectedSpecFallbackValues, setSelectedSpecFallbackValues] = useState({});
  const [specScrollStateByGroup, setSpecScrollStateByGroup] = useState({});
  const [variationModalOpen, setVariationModalOpen] = useState(false);
  const [variationPreview, setVariationPreview] = useState(null);
  const [variationQuantities, setVariationQuantities] = useState({});
  const [companyIntroduction, setCompanyIntroduction] = useState("");
  const [faqKnowledgeItems, setFaqKnowledgeItems] = useState([]);
  const [buyerReviews, setBuyerReviews] = useState([]);
  const [contactChannels, setContactChannels] = useState({
    email: String(storefrontContacts?.contactEmail || "").trim(),
    whatsapp: String(storefrontContacts?.contactWhatsapp || "").trim()
  });
  const [userToken, setUserToken] = useState("");
  const [viewerUserId, setViewerUserId] = useState(0);
  const [helpfulPendingReviewId, setHelpfulPendingReviewId] = useState(0);
  const [reviewActionTip, setReviewActionTip] = useState("");
  const specValuesContainerRefs = useRef(new Map());
  const [sampleForm, setSampleForm] = useState({
    fullName: "",
    email: "",
    whatsapp: "",
    company: "",
    requiredQuantity: "",
    message: ""
  });

  const optionLabel = (index) => renderTemplate("pdOptionLabel", { index: index + 1 });

  useEffect(() => {
    function syncViewerAuth() {
      const nextUserToken = localStorage.getItem("ft_user_token") || localStorage.getItem("ft_admin_token") || "";
      let nextViewerUserId = 0;
      try {
        const profile = JSON.parse(localStorage.getItem("ft_user_profile") || "null");
        nextViewerUserId = Number(profile?.id || 0);
      } catch (_err) {
        nextViewerUserId = 0;
      }

      setUserToken(nextUserToken);
      setViewerUserId(Number.isFinite(nextViewerUserId) && nextViewerUserId > 0 ? nextViewerUserId : 0);
    }

    syncViewerAuth();
    window.addEventListener("storage", syncViewerAuth);
    window.addEventListener("ft-user-auth-updated", syncViewerAuth);
    window.addEventListener("ft-admin-auth-updated", syncViewerAuth);

    return () => {
      window.removeEventListener("storage", syncViewerAuth);
      window.removeEventListener("ft-user-auth-updated", syncViewerAuth);
      window.removeEventListener("ft-admin-auth-updated", syncViewerAuth);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolveViewerProfile() {
      if (!userToken || viewerUserId > 0) {
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/auth/me?ts=${Date.now()}`, {
          cache: "no-store",
          headers: {
            Authorization: `Bearer ${userToken}`
          }
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const resolvedId = Number(data?.user?.id || 0);
        if (!cancelled && Number.isFinite(resolvedId) && resolvedId > 0) {
          setViewerUserId(resolvedId);
        }
      } catch (_err) {
        // Ignore profile lookup errors in storefront detail page.
      }
    }

    resolveViewerProfile();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, userToken, viewerUserId]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      setError("");
      setProductSpecs([]);

      try {
        const [response, relatedResponse, storefrontConfigResponse, specsResponse] = await Promise.all([
          fetch(
            `${apiBaseUrl}/products/${productId}?lang=${encodeURIComponent(storefrontLocale)}&currency=${encodeURIComponent(currency)}`
          ),
          fetch(
            `${apiBaseUrl}/products?lang=${encodeURIComponent(storefrontLocale)}&currency=${encodeURIComponent(currency)}`
          ),
          fetch(`${apiBaseUrl}/storefront/config`),
          fetch(`${apiBaseUrl}/products/item/specs?productId=${encodeURIComponent(String(productId))}`)
            .catch(() => null)
        ]);

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || t("pdLoadErrorProductDetail"));
        }
        const relatedData = await relatedResponse.json();

        let reviewRows = [];
        try {
          const reviewResponse = await fetch(
            `${apiBaseUrl}/products/${productId}/reviews?limit=40&viewerUserId=${encodeURIComponent(String(viewerUserId || 0))}&ts=${Date.now()}`,
            {
            cache: "no-store"
            }
          );
          if (reviewResponse.ok) {
            const reviewData = await reviewResponse.json();
            if (Array.isArray(reviewData?.reviews)) {
              reviewRows = reviewData.reviews;
            }
          }
        } catch (_reviewErr) {
          reviewRows = [];
        }
        const storefrontConfigData = await storefrontConfigResponse.json();

        let nextSpecs = [];
        if (specsResponse && specsResponse.ok) {
          const specsData = await specsResponse.json().catch(() => ({}));
          nextSpecs = Array.isArray(specsData?.specs) ? specsData.specs : [];
        }

        if (!relatedResponse.ok) {
          throw new Error(relatedData.message || t("pdLoadErrorRelatedProducts"));
        }

        if (!storefrontConfigResponse.ok) {
          throw new Error(storefrontConfigData.message || t("pdLoadErrorStorefrontConfig"));
        }

        if (!cancelled) {
          const detailItem = data?.product || null;
          const relatedItems = Array.isArray(relatedData?.products) ? relatedData.products : [];
          const storeSettings = storefrontConfigData?.storeSettings && typeof storefrontConfigData.storeSettings === "object"
            ? storefrontConfigData.storeSettings
            : {};
          const introText = String(storeSettings?.companyIntroduction || "").trim();
          const contactEmail = String(storeSettings?.contactEmail || storefrontContacts?.contactEmail || "").trim();
          const contactWhatsapp = String(storeSettings?.contactWhatsapp || storefrontContacts?.contactWhatsapp || "").trim();
          const faqItems = Array.isArray(storeSettings?.faqKnowledgeBase)
            ? storeSettings.faqKnowledgeBase
                .map((item) => ({
                  question: String(item?.question || item?.q || "").trim(),
                  answer: String(item?.answer || item?.a || "").trim()
                }))
                .filter((item) => item.question && item.answer)
            : [];

          setProduct(detailItem);
          setAllProducts(relatedItems);
          setProductSpecs(nextSpecs);
          setShowPricing(false);
          setCompanyIntroduction(introText);
          setContactChannels({
            email: contactEmail,
            whatsapp: contactWhatsapp
          });
          setFaqKnowledgeItems(faqItems);
          setBuyerReviews(Array.isArray(reviewRows) ? reviewRows : []);
        }
      } catch (err) {
        if (!cancelled) {
          setProduct(null);
          setProductSpecs([]);
          setAllProducts([]);
          setShowPricing(false);
          setCompanyIntroduction("");
          setContactChannels({
            email: String(storefrontContacts?.contactEmail || "").trim(),
            whatsapp: String(storefrontContacts?.contactWhatsapp || "").trim()
          });
          setFaqKnowledgeItems([]);
          setBuyerReviews([]);
          setError(err.message || t("pdLoadErrorProductDetail"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, productId, storefrontLocale, currency, viewerUserId, storefrontContacts?.contactEmail, storefrontContacts?.contactWhatsapp]);

  const whatsappInquiryPhone = useMemo(
    () => String(contactChannels?.whatsapp || "").replace(/\D/g, "").trim(),
    [contactChannels?.whatsapp]
  );

  const galleryImages = useMemo(() => {
    if (!product) {
      return [];
    }

    const preferredMainImages = Array.isArray(product.mainImages)
      ? product.mainImages.map((item) => String(item || "").trim()).filter(Boolean)
      : [];

    if (preferredMainImages.length) {
      return preferredMainImages;
    }

    const fallback = String(product.imageUrl || "").trim();
    return fallback ? [fallback] : [];
  }, [product]);

  const resolvedDetailVideoUrl = useMemo(() => {
    const raw = String(product?.detailVideoUrl || "").trim();
    if (!raw) {
      return "";
    }

    return raw;
  }, [product?.detailVideoUrl]);

  const resolvedMainVideoUrl = useMemo(() => {
    const raw = String(product?.mainVideoUrl || "").trim();
    if (!raw) {
      return "";
    }

    return raw;
  }, [product?.mainVideoUrl]);

  const hasMainVideo = Boolean(resolvedMainVideoUrl);
  const hasImages = galleryImages.length > 0;
  const hasAttributes = useMemo(() => {
    const hasParameters = Array.isArray(product?.parameters) && product.parameters.length > 0;
    const hasAttributeRows = Array.isArray(product?.attributes) && product.attributes.length > 0;
    return hasParameters || hasAttributeRows;
  }, [product]);

  const preferredMediaType = useMemo(() => {
    if (hasImages) {
      return "image";
    }

    if (hasMainVideo) {
      return "video";
    }

    if (hasAttributes) {
      return "attributes";
    }

    return "image";
  }, [hasAttributes, hasImages, hasMainVideo]);

  useEffect(() => {
    if (!galleryImages.length) {
      setActiveImage("");
      return;
    }

    setActiveImage(galleryImages[0]);
  }, [galleryImages]);

  useEffect(() => {
    setActiveMediaType(preferredMediaType);
    setZoomVisible(false);
  }, [preferredMediaType, product?.id]);

  useEffect(() => {
    setVideoLoadError(false);
  }, [product?.detailVideoUrl, product?.id]);

  useEffect(() => {
    if (activeMediaType !== "video" || !videoRef.current) {
      return;
    }

    const videoElement = videoRef.current;

    try {
      videoElement.currentTime = 0;
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } catch (_err) {
      // Ignore autoplay failures caused by browser policy.
    }
  }, [activeMediaType, resolvedMainVideoUrl]);

  useEffect(() => {
    setActiveInfoTab("attributes");
  }, [product?.id]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const productTitle = String(product?.name || "").trim();
    if (productTitle) {
      document.title = productTitle;
      return;
    }

    document.title = `${t("metaTitleProductDetail")} | ${t("metaTitleBrand")}`;
  }, [product?.name, storefrontLocale]);

  useEffect(() => {
    if (!product) {
      return;
    }

    setSampleForm({
      fullName: "",
      email: "",
      whatsapp: "",
      company: "",
      requiredQuantity: "",
      message: `${buildInquiryMessage()}\n${t("pdSampleDefaultMessage")}`
    });
    setSampleQuantity(1);
    setSampleSent(false);
    setSampleSubmitting(false);
    setSampleSubmitError("");
  }, [product]);

  useEffect(() => {
    if (!sampleModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sampleModalOpen]);

  useEffect(() => {
    if (!variationModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [variationModalOpen]);

  const displayAttributes = useMemo(() => {
    if (!product) {
      return [];
    }

    const rows = [
      { label: t("pdAttrProductName"), values: [product.name] },
      { label: t("pdAttrModelNo"), values: [product.model || product.sku] },
      { label: t("pdAttrCategory"), values: [product.category || t("productsOthers")] },
      { label: t("pdAttrManufacturer"), values: ["LOYA SPORT"] },
      ...(Array.isArray(product.parameters)
        ? product.parameters.map((item) => ({
            label: item.key,
            values: String(item.value || "")
              .split(/\s*,\s*|\s*\/\s*/)
              .filter(Boolean)
          }))
        : [])
    ];

    return rows.filter((item) => item.values.length > 0).slice(0, 6);
  }, [product]);

  const modalSummaryAttributes = useMemo(
    () => displayAttributes.filter((item) => item.label !== t("pdAttrProductName")).slice(0, 4),
    [displayAttributes]
  );

  const skuRows = useMemo(() => {
    if (!Array.isArray(product?.skus)) {
      return [];
    }

    const fallbackImages = Array.isArray(galleryImages) && galleryImages.length
      ? galleryImages
      : [product?.imageUrl].filter(Boolean);

    return product.skus
      .map((item, index) => ({
        skuCode: String(item?.skuCode || "").trim(),
        skuName: String(item?.skuName || "").trim(),
        isDefault: Boolean(item?.isDefault),
        isActive: item?.isActive !== false,
        price: Number(item?.price || 0),
        availableStockQty: Number(item?.availableStockQty || 0),
        optionValues: Array.isArray(item?.optionValues)
          ? item.optionValues.map((valueItem) => ({
              optionDefId: Number(valueItem?.optionDefId || 0),
              optionValueId: Number(valueItem?.optionValueId || 0),
              optionValue: String(valueItem?.optionValue || "").trim(),
              imageUrl: String(valueItem?.imageUrl || "").trim()
            }))
          : [],
        imageUrl:
          (Array.isArray(item?.optionValues)
            ? String(item.optionValues.find((valueItem) => String(valueItem?.imageUrl || "").trim())?.imageUrl || "").trim()
            : "") ||
          (fallbackImages.length ? fallbackImages[index % fallbackImages.length] : "")
      }))
      .filter((item) => item.skuCode || item.skuName)
      .sort((left, right) => Number(right.isDefault) - Number(left.isDefault));
  }, [galleryImages, product]);

  const selectedSku = useMemo(() => {
    if (!skuRows.length) {
      return null;
    }

    return skuRows.find((item) => item.skuCode === selectedSkuCode) || skuRows[0];
  }, [selectedSkuCode, skuRows]);

  function getParameterValue(...keys) {
    if (!Array.isArray(product?.parameters)) {
      return "";
    }

    const matched = product.parameters.find((item) =>
      keys.some((key) => String(item.key || "").toLowerCase() === String(key).toLowerCase())
    );

    return matched?.value || "";
  }

  const productSpecOptions = useMemo(() => {
    const rawSpecOptions = Array.isArray(productSpecs) && productSpecs.length
      ? productSpecs
      : Array.isArray(product?.specOptions)
        ? product.specOptions
        : [];

    if (!rawSpecOptions.length) {
      return [];
    }

    return rawSpecOptions
      .map((item) => {
        const valueItems = Array.isArray(item?.valueItems)
          ? item.valueItems
              .map((valueItem) => ({
                value: String(valueItem?.value || "").trim(),
                imageUrl: String(valueItem?.imageUrl || "").trim()
              }))
              .filter((valueItem) => valueItem.value || valueItem.imageUrl)
          : [];

        const fallbackValueItems = Array.isArray(item?.values)
          ? item.values
              .map((valueItem) => ({
                value: String(valueItem || "").trim(),
                imageUrl: ""
              }))
              .filter((valueItem) => valueItem.value)
          : [];

        const mergedValueItems = valueItems.length ? valueItems : fallbackValueItems;

        return {
          propName: String(item?.propName || "").trim(),
          propType: String(item?.propType || "").trim(),
          isRequired: item?.isRequired === true,
          values: mergedValueItems.map((valueItem) => valueItem.value).filter(Boolean),
          valueItems: mergedValueItems
        };
      })
      .filter((item) => item.propName && item.valueItems.length);
  }, [product?.specOptions, productSpecs]);

  const specificationRows = useMemo(() => {
    const rows = [];

    if (productSpecOptions.length) {
      rows.push(
        ...productSpecOptions.map((item) => ({
          key: item.propName,
          value: item.valueItems.map((valueItem) => valueItem.value).filter(Boolean).join(" / ")
        }))
      );
    }

    if (Array.isArray(product?.parameters)) {
      rows.push(...product.parameters);
    }

    if (Array.isArray(product?.attributes)) {
      rows.push(
        ...product.attributes.map((item) => ({
          key: item?.name || item?.key || "Attribute",
          value: item?.value || ""
        }))
      );
    }

    const dedup = new Set();
    return rows.filter((item) => {
      const key = `${String(item?.key || "").trim().toLowerCase()}::${String(item?.value || "").trim().toLowerCase()}`;
      if (!key || dedup.has(key)) {
        return false;
      }
      dedup.add(key);
      return Boolean(String(item?.key || "").trim() && String(item?.value || "").trim());
    });
  }, [product, productSpecOptions]);

  const minimumOrderQuantity = useMemo(() => product?.moq || getParameterValue("MOQ") || "100 pieces", [product]);

  const leadTimeRows = useMemo(() => {
    if (Array.isArray(product?.leadTimes) && product.leadTimes.length) {
      return product.leadTimes
        .map((item, index) => ({
          id: Number(item?.id || index + 1),
          minQty: Number(item?.minQty || 0),
          maxQty: item?.maxQty === null || item?.maxQty === undefined || item?.maxQty === ""
            ? null
            : Number(item.maxQty),
          leadDays: Number(item?.leadDays || 0),
          sortOrder: Number(item?.sortOrder || 0)
        }))
        .filter((item) => Number.isFinite(item.leadDays) && item.leadDays > 0)
        .sort((left, right) => left.sortOrder - right.sortOrder);
    }

    const text = String(product?.tradeLeadTime || "").trim();
    if (!text) {
      return [];
    }

    const maxMatch = text.match(/<=?\s*(\d+(?:\.\d+)?)\s*[:：]\s*(\d+(?:\.\d+)?)/);
    if (maxMatch) {
      return [{
        id: 1,
        minQty: 0,
        maxQty: Number(maxMatch[1]),
        leadDays: Number(maxMatch[2]),
        sortOrder: 0
      }];
    }

    const ratioMatch = text.match(/^(\d+(?:\.\d+)?)\s*[:：]\s*(\d+(?:\.\d+)?)$/);
    if (ratioMatch) {
      return [{
        id: 1,
        minQty: 0,
        maxQty: Number(ratioMatch[1]),
        leadDays: Number(ratioMatch[2]),
        sortOrder: 0
      }];
    }

    const dayMatch = text.match(/(\d+(?:\.\d+)?)/);
    return dayMatch
      ? [{ id: 1, minQty: 0, maxQty: null, leadDays: Number(dayMatch[1]), sortOrder: 0 }]
      : [];
  }, [product?.leadTimes, product?.tradeLeadTime]);

  const leadTimeSummaryText = useMemo(() => {
    if (!leadTimeRows.length) {
      return t("pdLeadTimeOnRequest");
    }

    const firstRow = leadTimeRows[0];
    if (firstRow.maxQty !== null && Number.isFinite(firstRow.maxQty)) {
      return renderTemplate("pdLeadTimeUpTo", { qty: firstRow.maxQty, days: firstRow.leadDays });
    }

    if (firstRow.minQty > 0) {
      return renderTemplate("pdLeadTimeFrom", { qty: firstRow.minQty, days: firstRow.leadDays });
    }

    return renderTemplate("pdLeadTimeDaysOnly", { days: firstRow.leadDays });
  }, [leadTimeRows]);

  const effectiveUnitPrice = useMemo(() => {
    if (selectedSku && Number.isFinite(selectedSku.price) && selectedSku.price > 0) {
      return selectedSku.price;
    }
    return Number(product?.price || 0);
  }, [product, selectedSku]);

  const priceRangeLabel = useMemo(() => {
    if (!showPricing || !effectiveUnitPrice) {
      return t("pdContactForQuote");
    }

    const priceText = formatPrice(effectiveUnitPrice, product?.currency || currency);
    return renderTemplate("pdPerPiece", { price: priceText });
  }, [currency, effectiveUnitPrice, product, showPricing]);

  const optionGroups = useMemo(() => {
    if (!Array.isArray(product?.optionDefs)) {
      return [];
    }

    return product.optionDefs
      .map((optionDef) => ({
        id: Number(optionDef?.id || 0),
        name: String(optionDef?.name || optionDef?.code || "Option").trim() || "Option",
        values: Array.isArray(optionDef?.values)
          ? optionDef.values
              .map((valueItem) => ({
                id: Number(valueItem?.id || 0),
                value: String(valueItem?.value || valueItem?.code || "").trim(),
                imageUrl: String(valueItem?.imageUrl || "").trim()
              }))
              .filter((valueItem) => valueItem.value || valueItem.imageUrl)
          : []
      }))
      .filter((optionDef) => optionDef.values.length > 0);
  }, [product?.optionDefs]);

  const specificationSelectorGroups = useMemo(() => {
    if (optionGroups.length) {
      return optionGroups.map((group) => ({
        key: `variation-${group.id}`,
        id: Number(group.id || 0),
        source: "variation",
        name: group.name,
        values: group.values.map((valueItem) => ({
          id: Number(valueItem.id || 0),
          value: String(valueItem.value || "").trim(),
          imageUrl: String(valueItem.imageUrl || "").trim()
        }))
      }));
    }

    return productSpecOptions.map((item, groupIndex) => ({
      key: `spec-${groupIndex}`,
      id: groupIndex + 1,
      source: "fallback",
      name: item.propName,
      values: item.valueItems
        .map((valueItem, valueIndex) => ({
          id: valueIndex + 1,
          value: String(valueItem?.value || "").trim(),
          imageUrl: String(valueItem?.imageUrl || "").trim()
        }))
        .filter((valueItem) => valueItem.value || valueItem.imageUrl)
    })).filter((group) => group.values.length > 0);
  }, [optionGroups, productSpecOptions]);

  const modalOptionGroups = useMemo(() => {
    if (optionGroups.length) {
      return optionGroups.map((group) => ({
        ...group,
        source: "variation"
      }));
    }

    return specificationSelectorGroups.map((group) => ({
      id: Number(group.id || 0),
      key: group.key,
      source: "fallback",
      name: group.name,
      values: group.values.map((valueItem) => ({
        id: Number(valueItem.id || 0),
        value: String(valueItem.value || "").trim(),
        imageUrl: String(valueItem.imageUrl || "").trim()
      }))
    }));
  }, [optionGroups, specificationSelectorGroups]);

  const skuByOptionIdentity = useMemo(() => {
    const map = new Map();
    skuRows.forEach((sku) => {
      sku.optionValues.forEach((valueItem) => {
        const optionDefId = Number(valueItem.optionDefId || 0);
        const optionValueId = Number(valueItem.optionValueId || 0);
        if (!optionDefId || !optionValueId) {
          return;
        }
        map.set(`${optionDefId}:${optionValueId}`, sku);
      });
    });
    return map;
  }, [skuRows]);

  useEffect(() => {
    if (!optionGroups.length) {
      setSelectedOptionValueIds({});
      setVariationPreview(null);
      setVariationQuantities({});
      return;
    }

    setSelectedOptionValueIds((current) => {
      const next = { ...current };
      optionGroups.forEach((group) => {
        const groupId = Number(group.id || 0);
        const currentValueId = Number(next[groupId] || 0);
        const hasCurrent = group.values.some((valueItem) => Number(valueItem.id || 0) === currentValueId);
        if (!hasCurrent) {
          const firstValue = group.values[0];
          next[groupId] = Number(firstValue?.id || 0);
        }
      });
      return next;
    });

    setVariationPreview((current) => {
      const stillExists = current && optionGroups.some((group) =>
        group.values.some((valueItem) =>
          Number(group.id || 0) === Number(current.groupId || 0) && Number(valueItem.id || 0) === Number(current.valueId || 0)
        )
      );
      if (stillExists) {
        return current;
      }

      const firstGroup = optionGroups[0];
      const firstValue = firstGroup?.values?.[0];
      if (!firstGroup || !firstValue) {
        return null;
      }

      return {
        groupId: Number(firstGroup.id || 0),
        valueId: Number(firstValue.id || 0),
        groupName: firstGroup.name,
        valueName: firstValue.value || "",
        imageUrl: firstValue.imageUrl || ""
      };
    });

    setVariationQuantities({});
  }, [optionGroups, product?.id]);

  useEffect(() => {
    if (!specificationSelectorGroups.length) {
      setSelectedSpecFallbackValues({});
      return;
    }

    setSelectedSpecFallbackValues((current) => {
      const next = { ...current };
      specificationSelectorGroups.forEach((group) => {
        if (group.source !== "fallback") {
          return;
        }

        const currentValueId = Number(next[group.key] || 0);
        const exists = group.values.some((valueItem) => Number(valueItem.id || 0) === currentValueId);
        if (!exists) {
          next[group.key] = Number(group.values[0]?.id || 0);
        }
      });
      return next;
    });
  }, [product?.id, specificationSelectorGroups]);

  const totalVariationQuantity = useMemo(
    () => Object.values(variationQuantities).reduce((sum, qty) => sum + (Number(qty) || 0), 0),
    [variationQuantities]
  );

  const ladderPriceRows = useMemo(() => {
    if (Array.isArray(product?.tierPrices) && product.tierPrices.length) {
      return product.tierPrices
        .map((item, index) => ({
          id: Number(item?.id || index + 1),
          qty: Number(item?.minQty || 0),
          unitPrice: Number(item?.unitPrice || 0),
          priceUnit: String(item?.priceUnit || product?.priceUnitCode || "piece").trim() || "piece"
        }))
        .filter((item) => Number.isFinite(item.qty) && item.qty > 0 && Number.isFinite(item.unitPrice) && item.unitPrice > 0)
        .sort((left, right) => left.qty - right.qty);
    }

    if (!showPricing || !effectiveUnitPrice) {
      return [];
    }

    const minimumText = String(minimumOrderQuantity || "");
    const minimumNumeric = Number(minimumText.replace(/[^\d.]/g, "")) || 100;
    const tiers = [
      { qty: minimumNumeric, multiplier: 1 },
      { qty: minimumNumeric * 2, multiplier: 0.96 },
      { qty: minimumNumeric * 5, multiplier: 0.91 }
    ];

    return tiers.map((tier, index) => ({
      id: index + 1,
      qty: tier.qty,
      unitPrice: Number((effectiveUnitPrice * tier.multiplier).toFixed(2)),
      priceUnit: String(product?.priceUnitCode || "piece").trim() || "piece"
    }));
  }, [effectiveUnitPrice, minimumOrderQuantity, product?.priceUnitCode, product?.tierPrices, showPricing]);

  const ladderPricingCards = useMemo(() => {
    if (!ladderPriceRows.length) {
      return [];
    }

    return ladderPriceRows.map((row, index) => {
      const nextRow = ladderPriceRows[index + 1] || null;
      const startQty = Number(row.qty || 0);
      let rangeLabel = `${startQty}+ pieces`;

      if (nextRow) {
        const endQty = Math.max(startQty, Number(nextRow.qty || 0) - 1);
        rangeLabel = `${startQty} - ${endQty} pieces`;
      } else if (index === ladderPriceRows.length - 1) {
        rangeLabel = `>= ${startQty} pieces`;
      }

      const displayPrice = formatPriceParts(row.unitPrice, product?.currency || currency);

      return {
        id: row.id,
        rangeLabel,
        displayPrice
      };
    });
  }, [currency, ladderPriceRows, product?.currency]);

  const publicReviews = useMemo(() => {
    if (!Array.isArray(buyerReviews)) {
      return [];
    }

    return buyerReviews
      .map((item) => ({
        id: Number(item?.id || 0),
        reviewerName: String(item?.reviewerName || "").trim() || t("pdAnonymousBuyer"),
        country: String(item?.country || "").trim(),
        rating: Math.max(1, Math.min(5, Number(item?.rating || 5) || 5)),
        variationText: String(item?.variationText || "").trim(),
        reviewText: String(item?.reviewText || "").trim(),
        reviewDate: String(item?.reviewDate || "").slice(0, 10),
        helpfulCount: Math.max(0, Number(item?.helpfulCount || 0) || 0),
        helpfulByCurrentUser: item?.helpfulByCurrentUser === true,
        verifiedPurchase: item?.verifiedPurchase !== false,
        repeatBuyer: item?.repeatBuyer === true
      }))
      .filter((item) => item.reviewText);
  }, [buyerReviews]);

  async function toggleReviewHelpful(reviewId) {
    const normalizedReviewId = Number(reviewId || 0);
    if (!normalizedReviewId) {
      return;
    }

    if (!userToken) {
      setReviewActionTip(t("pdReviewTipSignIn"));
      return;
    }

    if (helpfulPendingReviewId === normalizedReviewId) {
      return;
    }

    setHelpfulPendingReviewId(normalizedReviewId);
    try {
      const response = await fetch(`${apiBaseUrl}/products/${productId}/reviews/${normalizedReviewId}/helpful`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`
        }
      });

      const data = await response.json().catch(() => ({}));
      if (response.status === 401) {
        setReviewActionTip(t("pdReviewTipSessionExpired"));
        return;
      }
      if (response.status === 403) {
        setReviewActionTip(data?.message || t("pdReviewTipNoPermission"));
        return;
      }
      if (!response.ok) {
        throw new Error(data.message || t("pdReviewTipUpdateFailed"));
      }

      const nextHelpfulCount = Math.max(0, Number(data?.helpfulCount || 0) || 0);
      const nextHelpfulByCurrentUser = data?.helpfulByCurrentUser === true;
      setReviewActionTip("");
      setBuyerReviews((current) => current.map((item) => (
        Number(item?.id || 0) === normalizedReviewId
          ? {
              ...item,
              helpfulCount: nextHelpfulCount,
              helpfulByCurrentUser: nextHelpfulByCurrentUser
            }
          : item
      )));
    } catch (_err) {
      // Keep UX silent like marketplace behavior when write fails.
    } finally {
      setHelpfulPendingReviewId(0);
    }
  }

  const specDisplayOptions = useMemo(() => {
    if (productSpecOptions.length) {
      return productSpecOptions.map((item) => ({
        title: item.propName,
        detailLabel: t("pdSpecValues"),
        detailValue: item.valueItems.map((valueItem) => valueItem.value).filter(Boolean).join(" / "),
        requirement: item.isRequired ? t("pdSpecRequired") : t("pdSpecOptional")
      }));
    }

    if (optionGroups.length) {
      return optionGroups
        .map((group) => ({
          title: group.name,
          detailLabel: t("pdSpecValues"),
          detailValue: group.values
            .map((valueItem) => String(valueItem?.value || "").trim())
            .filter(Boolean)
            .join(" / "),
          requirement: t("pdSpecRequired")
        }))
        .filter((item) => item.detailValue);
    }

    return [];
  }, [optionGroups, productSpecOptions]);

  const supplierCapabilities = useMemo(
    () => [
      t("pdCapabilityCustomLogo"),
      t("pdCapabilitySampleDev"),
      t("pdCapabilityPackagingCoordination"),
      t("pdCapabilityExportFollowUp")
    ],
    [storefrontLocale]
  );

  const detailAttributeEntries = useMemo(() => {
    const databaseAttributes = Array.isArray(product?.attributes)
      ? product.attributes
          .map((item) => ({
            label: String(item?.name || item?.key || "").trim(),
            value: String(item?.value || "").trim()
          }))
          .filter((item) => item.label && item.value)
      : [];

    if (databaseAttributes.length) {
      return databaseAttributes.slice(0, 16);
    }

    return specificationRows
      .map((item) => ({
        label: String(item?.key || "").trim(),
        value: String(item?.value || "").trim()
      }))
      .filter((item) => item.label && item.value)
      .slice(0, 16);
  }, [product?.attributes, specificationRows]);

  const attributeGridRows = useMemo(() => {
    const rows = [];

    for (let index = 0; index < detailAttributeEntries.length; index += 2) {
      rows.push(detailAttributeEntries.slice(index, index + 2));
    }

    return rows;
  }, [detailAttributeEntries]);

  const packagingDetailEntries = useMemo(
    () => [
      { label: t("pdPackagingSellingUnit"), value: t("pdPackagingSingleItem") },
      { label: t("pdPackagingType"), value: product?.packagingType || getParameterValue("Packing") || t("pdPackagingTypeFallback") },
      { label: t("pdPackagingLoadingAdvice"), value: product?.loadingAdvice || t("pdPackagingLoadingAdviceFallback") },
      { label: t("pdPackagingLeadTime"), value: leadTimeSummaryText }
    ],
    [getParameterValue, leadTimeSummaryText, product, storefrontLocale]
  );

  const productDetailParagraphs = useMemo(() => {
    const explicit = String(product?.detailContent || "")
      .split(/\r?\n+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (explicit.length) {
      return explicit;
    }

    const descriptionText = String(product?.description || "").trim();
    return descriptionText ? [descriptionText] : [];
  }, [product]);

  const serviceCommitments = useMemo(() => {
    if (Array.isArray(product?.serviceCommitments) && product.serviceCommitments.length) {
      return product.serviceCommitments;
    }

    return supplierCapabilities;
  }, [product, supplierCapabilities]);

  const mediaItems = useMemo(() => {
    const items = [];

    if (hasImages) {
      galleryImages.forEach((imageUrl, index) => {
        items.push({
          id: `image-${index}`,
          type: "image",
          label: renderTemplate("pdMediaPhotoLabel", { index: index + 1 }),
          imageUrl
        });
      });
    }

    if (hasMainVideo) {
      items.push({
        id: "video",
        type: "video",
        label: t("pdMediaVideoLabel"),
        previewImage: galleryImages[0] || product?.imageUrl || ""
      });
    }

    if (hasAttributes) {
      items.push({
        id: "attributes",
        type: "attributes",
        label: t("pdMediaAttributesLabel")
      });
    }

    return items;
  }, [galleryImages, hasAttributes, hasImages, hasMainVideo, product]);

  const packagingPoints = useMemo(() => {
    if (!product) {
      return [];
    }

    return [
      product.packagingType ? renderTemplate("pdPackagingPointPackaging", { value: String(product.packagingType).trim() }) : "",
      product.loadingAdvice ? renderTemplate("pdPackagingPointLoadingAdvice", { value: String(product.loadingAdvice).trim() }) : "",
      product.logisticsNotes ? String(product.logisticsNotes).trim() : "",
      product.shippingPort
        ? renderTemplate("pdPackagingPointShippingPort", { value: String(product.shippingPort).trim() })
        : t("pdPackagingPointShippingPortNegotiable"),
      product.tradeLeadTime ? renderTemplate("pdPackagingPointLeadTime", { value: leadTimeSummaryText }) : ""
    ].filter(Boolean);
  }, [leadTimeSummaryText, product, storefrontLocale]);

  const relatedProducts = useMemo(() => {
    if (!product || !allProducts.length) {
      return [];
    }

    const sameCategory = allProducts.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const fallback = allProducts.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );

    return [...sameCategory, ...fallback].slice(0, 8);
  }, [allProducts, product]);

  useEffect(() => {
    if (!skuRows.length) {
      setSelectedSkuCode("");
      return;
    }

    setSelectedSkuCode((current) => {
      if (current && skuRows.some((item) => item.skuCode === current)) {
        return current;
      }
      return skuRows[0].skuCode;
    });
  }, [skuRows]);

  useEffect(() => {
    if (selectedSku?.imageUrl) {
      setActiveImage(selectedSku.imageUrl);
      setActiveMediaType("image");
    }
  }, [selectedSku]);

  function buildInquiryMessage() {
    if (!product) {
      return "";
    }

    return [
      t("pdInquiryHello"),
      renderTemplate("pdInquiryProduct", { value: product.name }),
      renderTemplate("pdInquirySku", { value: selectedSku?.skuCode || product.sku }),
      renderTemplate("pdInquirySkuName", { value: selectedSku?.skuName || "-" }),
      renderTemplate("pdInquiryModel", { value: product.model || "-" }),
      renderTemplate("pdInquiryQuantity", { value: "" })
    ].join("\n");
  }

  function openWhatsApp() {
    if (!product || typeof window === "undefined" || !whatsappInquiryPhone) {
      return;
    }

    const text = encodeURIComponent(buildInquiryMessage());
    window.open(`https://api.whatsapp.com/send?phone=${whatsappInquiryPhone}&text=${text}`, "_blank", "noopener,noreferrer");
  }

  function openEmail() {
    if (!product || typeof window === "undefined" || !contactChannels.email) {
      return;
    }

    const subject = encodeURIComponent(renderTemplate("pdEmailSubject", {
      sku: selectedSku?.skuCode || product.sku,
      name: product.name
    }));
    const body = encodeURIComponent(buildInquiryMessage());
    window.location.href = `mailto:${encodeURIComponent(contactChannels.email)}?subject=${subject}&body=${body}`;
  }

  function handleImageMove(event) {
    if (!mainImageRef.current) {
      return;
    }

    const rect = mainImageRef.current.getBoundingClientRect();
    const relativeX = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const relativeY = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
    const lensWidth = 28;
    const lensHeight = 34;

    setZoomVisible(true);
    setZoomPosition({
      x: relativeX * 100,
      y: relativeY * 100
    });
    setZoomLens({
      left: Math.min(Math.max(relativeX * 100 - lensWidth / 2, 0), 100 - lensWidth),
      top: Math.min(Math.max(relativeY * 100 - lensHeight / 2, 0), 100 - lensHeight),
      width: lensWidth,
      height: lensHeight
    });
  }

  function handleSampleFieldChange(field, value) {
    if (sampleSent) {
      setSampleSent(false);
    }
    if (sampleSubmitError) {
      setSampleSubmitError("");
    }
    setSampleForm((current) => ({
      ...current,
      [field]: value
    }));
  }

  async function handleSampleSubmit(event) {
    event.preventDefault();
    if (!sampleForm.fullName.trim() || !sampleForm.email.trim() || !sampleForm.whatsapp.trim() || !sampleForm.message.trim()) {
      return;
    }

    if (!apiBaseUrl || !product) {
      setSampleSent(false);
      setSampleSubmitError(t("pdInquirySubmitFailed"));
      return;
    }

    setSampleSubmitting(true);
    setSampleSubmitError("");
    setSampleSent(false);

    const productSku = String(selectedSku?.skuCode || product.sku || "-").trim();
    const normalizedQuantity = String(sampleForm.requiredQuantity || "").trim();
    const inquiryMessageLines = [
      sampleForm.message,
      renderTemplate("pdSampleContactPerson", { value: sampleForm.fullName.trim() }),
      renderTemplate("pdSampleWhatsapp", { value: sampleForm.whatsapp.trim() }),
      renderTemplate("pdSampleCompany", { value: String(sampleForm.company || "-").trim() || "-" }),
      renderTemplate("pdSampleRequestedQuantity", { value: normalizedQuantity || String(sampleQuantity) }),
      renderTemplate("pdSampleSelectorQuantity", { value: sampleQuantity })
    ];

    try {
      const response = await fetch(`${apiBaseUrl}/inquiries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          contactName: sampleForm.fullName.trim(),
          contactEmail: sampleForm.email.trim(),
          contactCompany: sampleForm.company.trim(),
          inquiryProduct: `${product.name} (${productSku})`,
          message: inquiryMessageLines.filter(Boolean).join("\n"),
          sourcePage: typeof window !== "undefined" ? window.location.pathname : `/products/${productId}`
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(String(data?.message || t("pdInquirySubmitFailed")));
      }

      setSampleSent(true);
      setSampleForm((current) => ({
        ...current,
        fullName: "",
        email: "",
        whatsapp: "",
        company: "",
        requiredQuantity: "",
        message: `${buildInquiryMessage()}\n${t("pdSampleDefaultMessage")}`
      }));
    } catch (_error) {
      setSampleSent(false);
      setSampleSubmitError(t("pdInquirySubmitFailed"));
    } finally {
      setSampleSubmitting(false);
    }
  }

  function scrollRelatedProducts(direction) {
    if (!relatedTrackRef.current) {
      return;
    }

    relatedTrackRef.current.scrollBy({
      left: direction * 320,
      behavior: "smooth"
    });
  }

  function handleMediaSelect(item) {
    if (item.type === "image") {
      setActiveImage(item.imageUrl || "");
      setActiveMediaType("image");
      return;
    }

    setZoomVisible(false);
    setActiveMediaType(item.type);
  }

  function switchMediaType(type) {
    if (type === "video" && !hasMainVideo) {
      if (hasImages) {
        setActiveMediaType("image");
        return;
      }

      if (hasAttributes) {
        setActiveMediaType("attributes");
      }
      return;
    }

    if (type === "image" && !hasImages) {
      if (hasAttributes) {
        setActiveMediaType("attributes");
      }
      return;
    }

    if (type === "attributes" && !hasAttributes) {
      if (hasImages) {
        setActiveMediaType("image");
        return;
      }

      if (hasMainVideo) {
        setActiveMediaType("video");
      }
      return;
    }

    if (type === "image" && hasImages && !activeImage) {
      setActiveImage(galleryImages[0]);
    }

    if (type === "video") {
      setVideoLoadError(false);
    }

    setZoomVisible(false);
    setActiveMediaType(type);
  }

  function retryVideoPlayback() {
    setVideoLoadError(false);

    if (videoRef.current) {
      try {
        videoRef.current.load();
      } catch (_err) {
        // Ignore manual reload failures.
      }
    }

    if (contentVideoRef.current) {
      try {
        contentVideoRef.current.load();
      } catch (_err) {
        // Ignore manual reload failures.
      }
    }
  }

  function handleVariationSelect(optionGroup, valueItem) {
    const groupId = Number(optionGroup?.id || 0);
    const valueId = Number(valueItem?.id || 0);
    if (!groupId || !valueId) {
      return;
    }

    setSelectedOptionValueIds((current) => ({
      ...current,
      [groupId]: valueId
    }));

    if (valueItem?.imageUrl) {
      setActiveImage(valueItem.imageUrl);
      setActiveMediaType("image");
    }
  }

  function handleVariationValuesWheel(event) {
    const container = event.currentTarget;
    if (!container) {
      return;
    }

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      container.scrollLeft += event.deltaY;
    }
  }

  function updateSpecScrollMaskState(groupKey, container) {
    if (!groupKey || !container) {
      return;
    }

    const left = Number(container.scrollLeft || 0);
    const maxLeft = Math.max(0, Number(container.scrollWidth || 0) - Number(container.clientWidth || 0));
    const hasLeft = left > 2;
    const hasRight = left < (maxLeft - 2);

    setSpecScrollStateByGroup((current) => {
      const previous = current[groupKey] || { hasLeft: false, hasRight: false };
      if (previous.hasLeft === hasLeft && previous.hasRight === hasRight) {
        return current;
      }

      return {
        ...current,
        [groupKey]: { hasLeft, hasRight }
      };
    });
  }

  function registerSpecValuesContainer(groupKey, node) {
    if (!groupKey) {
      return;
    }

    if (!node) {
      specValuesContainerRefs.current.delete(groupKey);
      return;
    }

    specValuesContainerRefs.current.set(groupKey, node);
    updateSpecScrollMaskState(groupKey, node);
  }

  function stepSpecValues(groupKey, direction) {
    const container = specValuesContainerRefs.current.get(groupKey);
    if (!container) {
      return;
    }

    const firstButton = container.querySelector("button");
    const computed = window.getComputedStyle(container);
    const gap = Number.parseFloat(computed.columnGap || computed.gap || "0") || 0;
    const step = Math.max(24, Number(firstButton?.offsetWidth || 0) + gap);

    container.scrollBy({
      left: direction * step,
      behavior: "smooth"
    });

    window.requestAnimationFrame(() => {
      updateSpecScrollMaskState(groupKey, container);
    });
  }

  function handleSpecValuesScroll(groupKey, event) {
    const container = event.currentTarget;
    updateSpecScrollMaskState(groupKey, container);
  }

  function handleSpecValuesMouseEnter(event) {
    const container = event.currentTarget;
    if (!container) {
      return;
    }

    container.dataset.lastPointerX = String(event.clientX || 0);
    container.dataset.lastStepTs = String(Date.now());
    updateSpecScrollMaskState(container.dataset.specGroupKey || "", container);
  }

  function handleSpecValuesMouseMove(event) {
    const container = event.currentTarget;
    if (!container) {
      return;
    }

    const lastPointerX = Number(container.dataset.lastPointerX || event.clientX || 0);
    const deltaX = Number(event.clientX || 0) - lastPointerX;
    const direction = deltaX > 0 ? 1 : deltaX < 0 ? -1 : 0;
    if (!direction || Math.abs(deltaX) < 16) {
      return;
    }

    const now = Date.now();
    const lastStepTs = Number(container.dataset.lastStepTs || 0);
    if (now - lastStepTs < 85) {
      return;
    }

    const hoveredButton = event.target instanceof Element ? event.target.closest("button") : null;
    if (!hoveredButton || !container.contains(hoveredButton)) {
      container.dataset.lastPointerX = String(event.clientX || 0);
      return;
    }

    const buttons = Array.from(container.querySelectorAll("button"));
    if (!buttons.length) {
      container.dataset.lastPointerX = String(event.clientX || 0);
      return;
    }

    const containerLeft = Number(container.scrollLeft || 0);
    const viewportLeft = containerLeft;
    const viewportRight = viewportLeft + Number(container.clientWidth || 0);
    const firstVisibleIndex = buttons.findIndex((button) => {
      const left = Number(button.offsetLeft || 0);
      const right = left + Number(button.offsetWidth || 0);
      return right > viewportLeft + 1;
    });
    const lastVisibleIndex = (() => {
      let lastIndex = -1;
      buttons.forEach((button, index) => {
        const left = Number(button.offsetLeft || 0);
        if (left < viewportRight - 1) {
          lastIndex = index;
        }
      });
      return lastIndex;
    })();

    const hoveredIndex = buttons.indexOf(hoveredButton);
    const canStepRight = direction > 0 && hoveredIndex === lastVisibleIndex;
    const canStepLeft = direction < 0 && hoveredIndex === firstVisibleIndex;
    if (!canStepLeft && !canStepRight) {
      container.dataset.lastPointerX = String(event.clientX || 0);
      return;
    }

    const firstButton = container.querySelector("button");
    const computed = window.getComputedStyle(container);
    const gap = Number.parseFloat(computed.columnGap || computed.gap || "0") || 0;
    const step = Math.max(24, Number(firstButton?.offsetWidth || 0) + gap);

    container.scrollBy({
      left: direction * step,
      behavior: "smooth"
    });

    container.dataset.lastPointerX = String(event.clientX || 0);
    container.dataset.lastStepTs = String(now);
    window.requestAnimationFrame(() => {
      updateSpecScrollMaskState(container.dataset.specGroupKey || "", container);
    });
  }

  function handleSpecValuesMouseLeave(event) {
    const container = event.currentTarget;
    if (!container) {
      return;
    }

    delete container.dataset.lastPointerX;
    delete container.dataset.lastStepTs;
    updateSpecScrollMaskState(container.dataset.specGroupKey || "", container);
  }

  useEffect(() => {
    specificationSelectorGroups.forEach((group) => {
      const container = specValuesContainerRefs.current.get(group.key);
      if (container) {
        updateSpecScrollMaskState(group.key, container);
      }
    });
  }, [specificationSelectorGroups]);

  function openVariationModal(optionGroup, valueItem, options = {}) {
    const shouldSyncVariation = options.syncVariation !== false;
    if (shouldSyncVariation && optionGroup?.source !== "fallback") {
      handleVariationSelect(optionGroup, valueItem);
    }

    setVariationPreview({
      groupId: Number(optionGroup?.id || 0),
      valueId: Number(valueItem?.id || 0),
      groupName: String(optionGroup?.name || "Option").trim(),
      valueName: String(valueItem?.value || "").trim(),
      imageUrl: String(valueItem?.imageUrl || "").trim()
    });

    const quantityKey = getVariationQuantityKey(optionGroup, valueItem);
    if (quantityKey) {
      setVariationQuantities((current) => (
        Number(current[quantityKey] || 0) > 0
          ? current
          : { ...current, [quantityKey]: 1 }
      ));
    }

    setVariationModalOpen(true);
  }

  function handleSpecificationOptionSelect(group, valueItem) {
    if (group.source === "variation") {
      openVariationModal(group, valueItem, { syncVariation: true });
      return;
    }

    setSelectedSpecFallbackValues((current) => ({
      ...current,
      [group.key]: Number(valueItem?.id || 0)
    }));

    openVariationModal(group, valueItem, { syncVariation: false });
  }

  function openDefaultVariationModal() {
    if (!modalOptionGroups.length) {
      return;
    }

    const firstGroup = modalOptionGroups[0];
    const selectedValueId = firstGroup.source === "variation"
      ? Number(selectedOptionValueIds[firstGroup.id] || 0)
      : Number(selectedSpecFallbackValues[firstGroup.key] || 0);
    const selectedValue =
      firstGroup.values.find((valueItem) => Number(valueItem.id || 0) === selectedValueId) ||
      firstGroup.values[0];

    if (!selectedValue) {
      return;
    }

    openVariationModal(firstGroup, selectedValue, {
      syncVariation: firstGroup.source !== "fallback"
    });
  }

  function resolveSkuForVariation(group, valueItem) {
    const optionDefId = Number(group?.id || 0);
    const optionValueId = Number(valueItem?.id || 0);
    const byRelation = skuByOptionIdentity.get(`${optionDefId}:${optionValueId}`);
    if (byRelation) {
      return byRelation;
    }

    const valueText = String(valueItem?.value || "").trim().toLowerCase();
    if (!valueText) {
      return null;
    }

    return (
      skuRows.find((sku) => String(sku.skuName || "").trim().toLowerCase() === valueText) ||
      null
    );
  }

  function getVariationQuantityKey(group, valueItem) {
    const source = String(group?.source || "variation").trim() || "variation";
    const groupId = Number(group?.id || 0);
    const valueId = Number(valueItem?.id || 0);
    if (!groupId || !valueId) {
      return "";
    }
    return `${source}:${groupId}:${valueId}`;
  }

  function updateVariationQuantity(group, valueItem, nextValue) {
    const quantityKey = getVariationQuantityKey(group, valueItem);
    if (!quantityKey) {
      return;
    }

    const quantity = Math.max(0, Math.min(9999, Number(nextValue) || 0));
    setVariationQuantities((current) => ({
      ...current,
      [quantityKey]: quantity
    }));
  }

  function openVariationInquiry() {
    if (!product || typeof window === "undefined" || !whatsappInquiryPhone) {
      return;
    }

    const lines = [];
    modalOptionGroups.forEach((group) => {
      group.values.forEach((valueItem) => {
        const qtyKey = getVariationQuantityKey(group, valueItem);
        const qty = Number(variationQuantities[qtyKey] || 0);
        if (qty > 0) {
          lines.push(`${group.name}: ${valueItem.value || t("pdVariationLabel")} x ${qty}`);
        }
      });
    });

    const message = [
      t("pdVariationInquiryIntro"),
      renderTemplate("pdInquiryProduct", { value: product.name }),
      renderTemplate("pdInquirySku", { value: selectedSku?.skuCode || product.sku }),
      ...lines,
      !lines.length ? t("pdVariationInquiryQuoteLeadTime") : ""
    ].filter(Boolean).join("\n");

    window.open(`https://api.whatsapp.com/send?phone=${whatsappInquiryPhone}&text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  }

  const sectionVisibility = pageTemplateConfig?.sectionVisibility || {};
  const isDetailSectionVisible = (sectionKey) => sectionVisibility?.[sectionKey] !== false;

  return (
    <StorefrontLayout
      apiBaseUrl={apiBaseUrl}
      currency={currency}
      onChangeCurrency={setCurrency}
      showPricing={showPricing}
    >
      {loading ? <p className="state">{t("pdLoading")}</p> : null}
      {error ? <p className="state error">{error}</p> : null}

      {!loading && !error && product ? (
        <>
          <section className="product-detail-hero">
            {isDetailSectionVisible("media") ? (
            <div className="product-detail-media-column">
            <div className="product-detail-gallery">
              <div className="product-detail-thumbs">
                {mediaItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={[
                      activeMediaType === item.type && (item.type !== "image" || activeImage === item.imageUrl)
                        ? "active"
                        : "",
                      item.type === "video" ? "is-video" : "",
                      item.type === "attributes" ? "is-attributes" : ""
                    ].join(" ").trim()}
                    onClick={() => handleMediaSelect(item)}
                    aria-label={item.label}
                  >
                    {item.type === "image" ? <img src={item.imageUrl} alt={product.name} loading="lazy" /> : null}
                    {item.type === "video" ? (
                      <>
                        {item.previewImage ? <img src={item.previewImage} alt={product.name} loading="lazy" /> : null}
                        <span className="product-detail-thumb-overlay">
                          <span className="product-detail-thumb-icon">▶</span>
                            <span>{t("pdMediaVideoLabel")}</span>
                        </span>
                      </>
                    ) : null}
                    {item.type === "attributes" ? (
                      <span className="product-detail-thumb-attr-card">
                        <span className="product-detail-thumb-icon">≣</span>
                          <span>{t("pdMediaAttributesLabel")}</span>
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>

              <div className="product-detail-main-stage">
                {activeMediaType === "video" && hasMainVideo ? (
                  <div className="product-detail-main-video">
                    <span className="product-detail-brand-stamp">LOYA SPORT</span>
                    <video
                      ref={videoRef}
                      controls
                      playsInline
                      preload="metadata"
                      src={resolvedMainVideoUrl}
                      poster={galleryImages[0] || product.imageUrl || undefined}
                      onCanPlay={() => setVideoLoadError(false)}
                      onLoadedData={() => setVideoLoadError(false)}
                      onError={() => setVideoLoadError(true)}
                    />
                    {videoLoadError ? (
                      <div className="product-detail-video-fallback" role="status" aria-live="polite">
                        <p>{t("pdVideoFallback")}</p>
                        <div className="product-detail-video-fallback-actions">
                          <button type="button" className="button ghost" onClick={retryVideoPlayback}>{t("pdRetry")}</button>
                          <a className="button ghost" href={resolvedMainVideoUrl} target="_blank" rel="noreferrer">{t("pdOpenVideoUrl")}</a>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {activeMediaType === "image" && hasImages ? (
                  <>
                    <div
                      ref={mainImageRef}
                      className="product-detail-main-image"
                      onMouseEnter={() => setZoomVisible(true)}
                      onMouseLeave={() => setZoomVisible(false)}
                      onMouseMove={handleImageMove}
                    >
                      <span className="product-detail-brand-stamp">LOYA SPORT</span>
                      <img src={activeImage || product.imageUrl} alt={product.name} loading="lazy" />
                      {zoomVisible ? (
                        <span
                          className="product-detail-zoom-lens"
                          style={{
                            left: `${zoomLens.left}%`,
                            top: `${zoomLens.top}%`,
                            width: `${zoomLens.width}%`,
                            height: `${zoomLens.height}%`
                          }}
                        />
                      ) : null}
                    </div>

                    {zoomVisible ? (
                      <div
                        className="product-detail-zoom-panel"
                        style={{
                          backgroundImage: `url(${activeImage || product.imageUrl})`,
                          backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                        }}
                        aria-hidden="true"
                      />
                    ) : null}
                  </>
                ) : null}

                {activeMediaType === "attributes" && hasAttributes ? (
                  <div className="product-detail-main-attributes">
                    <span className="product-detail-brand-stamp">LOYA SPORT</span>
                    <div className="product-detail-main-attributes-card">
                      <div className="product-detail-main-attributes-head">
                        <span>{t("pdProductAttributes")}</span>
                        <strong>{product.category}</strong>
                      </div>
                      <table className="product-detail-inline-params-table">
                        <tbody>
                          {specificationRows.map((item) => (
                            <tr key={`media-${item.key}-${item.value}`}>
                              <th>{item.key}</th>
                              <td>{item.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                <div className="product-detail-media-tabs" role="tablist" aria-label={t("pdMediaTabsAria")}>
                  <button
                    type="button"
                    className={activeMediaType === "image" ? "active" : ""}
                    onClick={() => switchMediaType("image")}
                    disabled={!hasImages}
                  >
                    {t("pdMediaTabPhotos")}
                  </button>
                  <button
                    type="button"
                    className={activeMediaType === "video" ? "active" : ""}
                    onClick={() => switchMediaType("video")}
                    disabled={!hasMainVideo}
                  >
                    {t("pdMediaTabVideo")}
                  </button>
                  <button
                    type="button"
                    className={activeMediaType === "attributes" ? "active" : ""}
                    onClick={() => switchMediaType("attributes")}
                    disabled={!hasAttributes}
                  >
                    {t("pdMediaTabAttributes")}
                  </button>
                </div>
              </div>
            </div>

              <section className="product-detail-info-panel">
                <div className="product-detail-info-tabs" role="tablist" aria-label={t("pdInfoTabsAria")}>
                  <button type="button" className={activeInfoTab === "attributes" ? "active" : ""} onClick={() => setActiveInfoTab("attributes")}>{t("pdInfoTabAttributes")}</button>
                  <button type="button" className={activeInfoTab === "description" ? "active" : ""} onClick={() => setActiveInfoTab("description")}>{t("pdInfoTabDescription")}</button>
                  <button type="button" className={activeInfoTab === "supplier" ? "active" : ""} onClick={() => setActiveInfoTab("supplier")}>{t("pdInfoTabSupplier")}</button>
                  <button type="button" className={activeInfoTab === "reviews" ? "active" : ""} onClick={() => setActiveInfoTab("reviews")}>{t("pdInfoTabReviews")}</button>
                </div>

                {activeInfoTab === "attributes" ? (
                  <div className="product-detail-info-body">
                    <h3>{t("pdKeyAttributes")}</h3>
                    <div className="product-detail-attribute-table-wrap">
                      <table className="product-detail-attribute-table">
                        <tbody>
                          {attributeGridRows.map((row, rowIndex) => (
                            <tr key={`attr-row-${rowIndex}`}>
                              {row.map((item) => ([
                                <th key={`${item.label}-label`}>{item.label}</th>,
                                <td key={`${item.label}-value`}>{item.value}</td>
                              ]))}
                              {row.length === 1 ? [
                                <th key={`empty-label-${rowIndex}`}></th>,
                                <td key={`empty-value-${rowIndex}`}></td>
                              ] : null}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h4>{t("pdPackagingShippingInfo")}</h4>
                    <div className="product-detail-attribute-table-wrap">
                      <table className="product-detail-attribute-table compact">
                        <tbody>
                          {packagingDetailEntries.map((item, index) => {
                            const nextItem = packagingDetailEntries[index + 1];
                            if (index % 2 === 1) {
                              return null;
                            }

                            return (
                              <tr key={`packaging-row-${item.label}`}>
                                <th>{item.label}</th>
                                <td>{item.value}</td>
                                <th>{nextItem?.label || ""}</th>
                                <td>{nextItem?.value || ""}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {activeInfoTab === "description" ? (
                  <div className="product-detail-info-body product-detail-prose">
                    <h3>{t("pdProductDescription")}</h3>
                    {productDetailParagraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                ) : null}

                {activeInfoTab === "supplier" ? (
                  <div className="product-detail-info-body product-detail-prose">
                    <h3>{t("pdSupplierOverview")}</h3>
                    <p>{t("pdSupplierOverviewHint")}</p>
                  </div>
                ) : null}

                {activeInfoTab === "reviews" ? (
                  <div className="product-detail-info-body product-detail-prose">
                    <h3>{t("pdBuyerFeedback")}</h3>
                    {reviewActionTip ? <p className="product-detail-review-tip">{reviewActionTip}</p> : null}
                    {publicReviews.length ? (
                      <div className="product-detail-review-list">
                        {publicReviews.map((item) => (
                          <article key={`review-${item.id || item.reviewerName}`} className="product-detail-review-item">
                            <div className="product-detail-review-left">
                              <div className="product-detail-review-avatar">{String(item.reviewerName || "A").slice(0, 1).toUpperCase()}</div>
                              <p className="product-detail-review-name">{maskReviewerName(item.reviewerName)}</p>
                              <p className="product-detail-review-country">{item.country || "-"}</p>
                              {item.repeatBuyer ? <p className="product-detail-review-repeat">{t("pdRepeatBuyer")}</p> : null}
                            </div>

                            <div className="product-detail-review-main">
                              <div className="product-detail-review-head-row">
                                <p className="product-detail-review-stars" aria-label={renderTemplate("pdReviewRatingAria", { rating: item.rating })}>{"★".repeat(item.rating)}{"☆".repeat(Math.max(0, 5 - item.rating))}</p>
                                <span>{formatReviewDate(item.reviewDate)}</span>
                              </div>
                              <p className="product-detail-review-meta-line">{renderTemplate("pdReviewMetaColor", { value: item.variationText || t("pdReviewMetaColorFallback") })}</p>
                              <p className="product-detail-review-text">{item.reviewText}</p>
                              <div className="product-detail-review-reply">{t("pdReviewUnavailable")}</div>
                              <button
                                type="button"
                                className={`product-detail-review-helpful${item.helpfulByCurrentUser ? " active" : ""}`}
                                onClick={() => toggleReviewHelpful(item.id)}
                                disabled={helpfulPendingReviewId === item.id}
                              >
                                {renderTemplate("pdReviewHelpful", { count: item.helpfulCount })}
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <>
                        <p>{t("pdNoReviews")}</p>
                        <p>{t("pdNoReviewsHint")}</p>
                      </>
                    )}
                  </div>
                ) : null}
              </section>
            </div>
            ) : null}

            <article className="product-detail-summary">
              <div className="product-detail-summary-head">
                <p className="loya-product-kicker">{String(pageTemplateConfig?.inquiryTitle || "").trim()}</p>
                <h2>
                  <Link
                    className="product-detail-summary-title-link"
                    to={`/products/${encodeURIComponent(String(productId))}`}
                    state={location.state}
                    title={product?.name || t("pdTitleFallback")}
                    onClick={(event) => {
                      event.preventDefault();
                      if (typeof window !== "undefined") {
                        window.location.reload();
                      }
                    }}
                  >
                    {product?.name || t("pdTitleFallback")}
                  </Link>
                </h2>
                <div className="product-detail-summary-label-row">
                  <span className="product-detail-summary-tag">{t("pdCustomizable")}</span>
                  {skuRows.length ? <span className="product-detail-summary-badge">{skuRows.length} SKUs</span> : null}
                </div>
              </div>

              {isDetailSectionVisible("variations") && optionGroups.length ? (
                <section className="product-detail-variation-card" aria-label={t("pdVariationCardAria")}>
                  <div className="product-detail-variation-head">
                    <h4>{t("pdVariations")}</h4>
                    <button
                      type="button"
                      className="product-detail-variation-link"
                      onClick={openDefaultVariationModal}
                    >
                      {t("pdSelectNow")}
                    </button>
                  </div>

                  {optionGroups.map((optionGroup) => {
                    const groupId = Number(optionGroup.id || 0);
                    const selectedValueId = Number(selectedOptionValueIds[groupId] || 0);
                    const selectedValue =
                      optionGroup.values.find((valueItem) => Number(valueItem.id || 0) === selectedValueId) ||
                      optionGroup.values[0] ||
                      null;
                    const isVisualGroup = optionGroup.values.some((valueItem) => String(valueItem.imageUrl || "").trim());

                    return (
                      <div key={`variation-group-${optionGroup.id}`} className="product-detail-variation-group">
                        {isVisualGroup ? (
                          <p className="product-detail-variation-current">
                            <strong>{`${optionGroup.name}:`}</strong>
                            <span>{selectedValue?.value || t("pdSelectOption")}</span>
                          </p>
                        ) : (
                          <strong className="product-detail-variation-label">{optionGroup.name}</strong>
                        )}

                        <div
                          className={`product-detail-variation-values ${isVisualGroup ? "is-visual" : "is-text"}`}
                          onWheel={handleVariationValuesWheel}
                        >
                          {optionGroup.values.map((valueItem, index) => {
                            const isActive = Number(selectedOptionValueIds[groupId] || 0) === Number(valueItem.id || 0);
                            const fallbackLabel = optionLabel(index);

                            return (
                              <button
                                key={`variation-${optionGroup.id}-${valueItem.id || index}`}
                                type="button"
                                className={isActive ? "active" : ""}
                                onClick={() => openVariationModal(optionGroup, valueItem)}
                                aria-pressed={isActive}
                                title={valueItem.value || fallbackLabel}
                              >
                                {isVisualGroup && valueItem.imageUrl ? (
                                  <img src={valueItem.imageUrl} alt={valueItem.value || fallbackLabel} loading="lazy" />
                                ) : (
                                  <span>{valueItem.value || fallbackLabel}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </section>
              ) : null}

              {String(pageTemplateConfig?.inquiryDescription || "").trim() ? (
                <p className="product-detail-desc">{String(pageTemplateConfig?.inquiryDescription || "").trim()}</p>
              ) : null}

              {isDetailSectionVisible("pricing") && ladderPricingCards.length ? (
                <section className="product-detail-ladder-band" aria-label={t("pdLadderPricingAria")}>
                  {ladderPricingCards.map((row) => (
                    <article key={`ladder-band-${row.id}`}>
                      <span>{row.rangeLabel}</span>
                      <p>
                        <b>{row.displayPrice.symbol}</b>
                        {row.displayPrice.integer}
                        <sup>.{row.displayPrice.fraction}</sup>
                      </p>
                    </article>
                  ))}
                </section>
              ) : null}

              {isDetailSectionVisible("description") ? (
                <p className="product-detail-desc">{product.description}</p>
              ) : null}

              {isDetailSectionVisible("specs") ? (
              <section className="product-detail-spec-card">
                <div className="product-detail-customization product-detail-spec-values">
                  <h4>{t("pdProductSpecifications")}</h4>
                  <div className="product-detail-spec-selector-list">
                    {specificationSelectorGroups.map((group) => {
                      const isVisualGroup = group.values.some((valueItem) => String(valueItem.imageUrl || "").trim());
                      const selectedValueId = group.source === "variation"
                        ? Number(selectedOptionValueIds[group.id] || 0)
                        : Number(selectedSpecFallbackValues[group.key] || 0);
                      const selectedValue =
                        group.values.find((valueItem) => Number(valueItem.id || 0) === selectedValueId) ||
                        group.values[0] ||
                        null;
                      const scrollState = specScrollStateByGroup[group.key] || { hasLeft: false, hasRight: false };
                      const laneMaskClass = `product-detail-spec-values-track${scrollState.hasLeft ? " has-left-mask" : ""}${scrollState.hasRight ? " has-right-mask" : ""}`;

                      return (
                        <div key={group.key} className="product-detail-variation-group product-detail-spec-selector-group">
                          {isVisualGroup ? (
                            <p className="product-detail-variation-current">
                              <strong>{`${group.name}:`}</strong>
                                <span>{selectedValue?.value || t("pdSelectOption")}</span>
                            </p>
                          ) : (
                            <strong className="product-detail-variation-label">{group.name}</strong>
                          )}

                          <div className={laneMaskClass}>
                            {scrollState.hasLeft ? (
                              <button
                                type="button"
                                className="product-detail-spec-mask-arrow left"
                                onClick={() => stepSpecValues(group.key, -1)}
                                aria-label={t("pdSpecPrevAria")}
                              >
                                <span aria-hidden="true">&#8249;</span>
                              </button>
                            ) : <span className="product-detail-spec-mask-spacer left" aria-hidden="true" />}

                            <div
                              ref={(node) => registerSpecValuesContainer(group.key, node)}
                              data-spec-group-key={group.key}
                              className={`product-detail-variation-values ${isVisualGroup ? "is-visual" : "is-text"}`}
                              onWheel={handleVariationValuesWheel}
                              onMouseEnter={handleSpecValuesMouseEnter}
                              onMouseMove={handleSpecValuesMouseMove}
                              onMouseLeave={handleSpecValuesMouseLeave}
                              onScroll={(event) => handleSpecValuesScroll(group.key, event)}
                            >
                              {group.values.map((valueItem, index) => {
                                const fallbackLabel = optionLabel(index);
                                const isActive = Number(selectedValueId || 0) === Number(valueItem.id || 0);

                                return (
                                  <button
                                    key={`${group.key}-${valueItem.id || index}`}
                                    type="button"
                                    className={isActive ? "active" : ""}
                                    onClick={() => handleSpecificationOptionSelect(group, valueItem)}
                                    aria-pressed={isActive}
                                    title={valueItem.value || fallbackLabel}
                                  >
                                    {isVisualGroup && valueItem.imageUrl ? (
                                      <img src={valueItem.imageUrl} alt={valueItem.value || fallbackLabel} loading="lazy" />
                                    ) : (
                                      <span>{valueItem.value || fallbackLabel}</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>

                            {scrollState.hasRight ? (
                              <button
                                type="button"
                                className="product-detail-spec-mask-arrow right"
                                onClick={() => stepSpecValues(group.key, 1)}
                                aria-label={t("pdSpecNextAria")}
                              >
                                <span aria-hidden="true">&#8250;</span>
                              </button>
                            ) : <span className="product-detail-spec-mask-spacer right" aria-hidden="true" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="product-detail-verified-block">
                  <h4>{t("pdSupplierCapability")}</h4>
                  <ul className="product-detail-verified-list">
                    {serviceCommitments.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </section>
              ) : null}

              {isDetailSectionVisible("actions") ? (
              <div className="product-detail-summary-actions">
                <button type="button" className="button product-detail-sample-button" onClick={() => setSampleModalOpen(true)}>
                  {String(pageTemplateConfig?.secondaryCtaText || t("pdGetFreeSample")).trim()}
                </button>
                <div className="product-detail-link-actions">
                  <button type="button" className="button ghost" onClick={openWhatsApp}>{String(pageTemplateConfig?.primaryCtaText || t("pdWhatsAppInquiry")).trim()}</button>
                  <button type="button" className="button ghost" onClick={openEmail}>{t("pdEmailInquiry")}</button>
                </div>
              </div>
              ) : null}

              {isDetailSectionVisible("trustNote") && String(pageTemplateConfig?.trustNote || "").trim() ? (
                <p className="product-detail-review-tip">{String(pageTemplateConfig?.trustNote || "").trim()}</p>
              ) : null}
            </article>
          </section>

          {isDetailSectionVisible("content") ? (
          <section className="product-detail-content">
            {productDetailParagraphs.length ? (
            <article className="product-detail-panel">
              <h3>{t("pdProductDescription")}</h3>
              <div className="product-detail-prose">
                {productDetailParagraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
            ) : null}

            {packagingPoints.length ? (
            <article className="product-detail-panel">
              <h3>{t("pdPackagingDelivery")}</h3>
              <ul className="product-detail-list">
                {packagingPoints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            ) : null}

            {resolvedDetailVideoUrl ? (
            <article className="product-detail-panel">
              <h3>{t("pdProductVideo")}</h3>
                <>
                  <video
                    ref={contentVideoRef}
                    controls
                    playsInline
                    preload="metadata"
                    src={resolvedDetailVideoUrl}
                    onCanPlay={() => setVideoLoadError(false)}
                    onLoadedData={() => setVideoLoadError(false)}
                    onError={() => setVideoLoadError(true)}
                  />
                  {videoLoadError ? (
                    <p className="state error">
                      {t("pdVideoOpenLink")}
                      {" "}
                      <a href={resolvedDetailVideoUrl} target="_blank" rel="noreferrer">{t("pdOpenVideoUrl")}</a>
                    </p>
                  ) : null}
                </>
            </article>
            ) : null}

            {product.detailImages?.length ? (
            <article className="product-detail-panel">
              <h3>{t("pdSupplierDescriptions")}</h3>
                <div className="product-detail-images">
                  {product.detailImages.map((imageUrl) => (
                    <img key={imageUrl} src={imageUrl} alt={product.name} loading="lazy" />
                  ))}
                </div>
            </article>
            ) : null}

            {companyIntroduction || faqKnowledgeItems.length ? (
            <article className="product-detail-panel product-detail-company-faq-panel">
              <section className="product-detail-company-intro">
                <h3><span>{t("pdCompanyIntroduction")}</span></h3>
                {companyIntroduction ? (
                  String(companyIntroduction)
                    .split(/\r?\n+/)
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((line, index) => <p key={`intro-line-${index}`}>{line}</p>)
                ) : (
                  <p className="state">{t("pdNoCompanyIntroduction")}</p>
                )}
              </section>

              <section className="product-detail-company-faq">
                <h3><span>{t("pdFaq")}</span></h3>
                {faqKnowledgeItems.length ? (
                  <ol>
                    {faqKnowledgeItems.map((item, index) => (
                      <li key={`faq-${index}`}>
                        <strong>{item.question}</strong>
                        <p>{item.answer}</p>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="state">{t("pdNoFaq")}</p>
                )}
              </section>
            </article>
            ) : null}
          </section>
          ) : null}

          <ProductRelatedSection
            relatedProducts={{ items: relatedProducts, trackRef: relatedTrackRef }}
            scrollRelatedProducts={scrollRelatedProducts}
          />

          <ProductSampleModal
            open={sampleModalOpen}
            onClose={() => setSampleModalOpen(false)}
            product={product}
            activeImage={activeImage}
            modalSummaryAttributes={modalSummaryAttributes}
            sampleQuantity={sampleQuantity}
            setSampleQuantity={setSampleQuantity}
            sampleForm={sampleForm}
            onFieldChange={handleSampleFieldChange}
            onSubmit={handleSampleSubmit}
            sampleSent={sampleSent}
            submitPending={sampleSubmitting}
            submitError={sampleSubmitError}
          />

          {variationModalOpen && variationPreview ? (
            <div className="variation-picker-backdrop" onClick={() => setVariationModalOpen(false)}>
              <div className="variation-picker-dialog" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="variation-picker-close"
                  onClick={() => setVariationModalOpen(false)}
                  aria-label={t("pdCloseVariationSelector")}
                >
                  x
                </button>

                <div className="variation-picker-media">
                  <img
                    src={variationPreview.imageUrl || activeImage || product.imageUrl}
                    alt={variationPreview.valueName || product.name}
                  />
                  <p>{String(variationPreview.groupName || t("pdVariationLabel")).toLowerCase()}: {variationPreview.valueName || "-"}</p>
                </div>

                <aside className="variation-picker-panel">
                  <h3>{t("pdVariationSelectAndQty")}</h3>
                  <div className="variation-picker-title-divider" aria-hidden="true" />

                  {modalOptionGroups.map((group) => (
                    <section key={`picker-group-${group.id}`} className="variation-picker-group">
                      <h4>{group.name}</h4>
                      <div className="variation-picker-list">
                        {group.values.map((valueItem, index) => {
                          const qtyKey = getVariationQuantityKey(group, valueItem);
                          const qty = Number(variationQuantities[qtyKey] || 0);
                          const isPreview = Number(variationPreview.valueId || 0) === Number(valueItem.id || 0);
                          const matchedSku = resolveSkuForVariation(group, valueItem);
                          const fallbackLabel = optionLabel(index);
                          return (
                            <article key={`picker-item-${group.id}-${valueItem.id || index}`} className={isPreview ? "active" : ""}>
                              <button
                                type="button"
                                className="variation-picker-item-main"
                                onClick={() => {
                                  setVariationPreview({
                                    groupId: Number(group.id || 0),
                                    valueId: Number(valueItem.id || 0),
                                    groupName: group.name,
                                    valueName: valueItem.value || "",
                                    imageUrl: valueItem.imageUrl || ""
                                  });
                                  if (group.source !== "fallback") {
                                    handleVariationSelect(group, valueItem);
                                  }
                                  if (qtyKey) {
                                    setVariationQuantities((current) => (
                                      Number(current[qtyKey] || 0) > 0
                                        ? current
                                        : { ...current, [qtyKey]: 1 }
                                    ));
                                  }
                                }}
                              >
                                {valueItem.imageUrl ? <img src={valueItem.imageUrl} alt={valueItem.value || fallbackLabel} loading="lazy" /> : null}
                                <span>{valueItem.value || fallbackLabel}</span>
                              </button>

                              <div className="variation-picker-item-meta">
                                {group.source !== "fallback" && matchedSku ? (
                                  <small>
                                    {matchedSku.skuCode}
                                    {matchedSku.skuName ? ` / ${matchedSku.skuName}` : ""}
                                    {Number.isFinite(matchedSku.availableStockQty) ? renderTemplate("pdVariationStock", { count: Math.max(0, matchedSku.availableStockQty) }) : ""}
                                  </small>
                                ) : null}
                                <div className="variation-picker-qty">
                                  <button type="button" onClick={() => updateVariationQuantity(group, valueItem, qty - 1)}>-</button>
                                  <span>{qty}</span>
                                  <button type="button" onClick={() => updateVariationQuantity(group, valueItem, qty + 1)}>+</button>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  ))}

                  <div className="variation-picker-footer">
                    <p>
                      <span>{t("pdSelectedQuantity")}</span>
                      <strong>{totalVariationQuantity}</strong>
                    </p>
                    <div className="variation-picker-action-row">
                      <button type="button" className="button" onClick={openVariationInquiry}>
                        {t("pdSendInquiry")}
                      </button>
                      <button type="button" className="button ghost" onClick={openWhatsApp}>
                        {t("pdChatNow")}
                      </button>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </StorefrontLayout>
  );
}
